import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { ReviewTimelineService } from "./review-timeline.service";
import Fund from "../../interfaces/fund";
import { BehaviorSubject, combineLatest, Subscription } from "rxjs";
import { DataFormatService } from "../../services/data-format.service";
import OutletReviewCategory from "../../interfaces/outlet-review-category";
import ID from "../../types/id";
import OutletReviewTracker from "../../interfaces/outlet-review-tracker";
import Project from "../../interfaces/project";


export type TrackerTableItem = {
  row: number;
  outlet_review_tracker_id: ID;
  projectName: string;
  projectUrl: string;
  funds: Fund[];
  outlet_review_category_id:ID;
  city:string|null;
  state:string|null;
  sponsorName: string|null;
  sponsorUrl: string|null;
  equity:number|null;
  percentage:number|null;
  cumulative:number|null;
  status: string;
  cohn_reznick_review_status_id: ID|null;
  committee_memo_status_id: ID|null;
  third_party_review_date_completed: string|null;
  executive_report_distributed: boolean;
  committee_is_target: boolean;
  committee_date: string|null;
  close_is_target: boolean;
  close_date: string|null;
  investor_committee_date: string|null;
  analystName: string|null;
}
type TotalTableItem = {
  label: string;
  equity:number;
  percentage:number;
}

@Component({
    selector: 'review-timeline',
    templateUrl: './review-timeline.component.html',
    styleUrls: ['./review-timeline.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReviewTimelineComponent {
  public fund: Fund;
  public totalBeforeClose = 0.0;
  public total = 0.0;
  public totalPercent = 1.0;
  public totalPercentBeforeClose =0.0;
  private _items:TrackerTableItem[] = [];
  public items = new BehaviorSubject(this._items);
  public items$ = this.items.asObservable();
  public trackerSub:Subscription;

  headerColumns = [
    'blank',
    'blank',
    'blank',
    'blank',
    'equity',
    'percent',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
  ];
  columns = [
    'label',
    'equity',
    'percent',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
    'blank',
  ];
totalItems: TotalTableItem[] = [];
  constructor(private route: ActivatedRoute, public reviewTimelineService: ReviewTimelineService, public dataFormat: DataFormatService) {
  }

  ngOnInit() {
    let fund_id = this.route.snapshot.paramMap.get('id');
    if(fund_id) {
      this.reviewTimelineService.getReviewTrackerData(fund_id);
    }
    this.trackerSub = combineLatest({fund:this.reviewTimelineService.fund$, categories: this.reviewTimelineService.outletReviewCategories$, trackers: this.reviewTimelineService.trackers$}).subscribe(({fund, categories, trackers})=>{
        let sortedTrackers = trackers.slice().sort((a,b)=> {
            let a_cat = categories.find((cat)=>cat.id === a.outlet_review_category_id);
            let b_cat = categories.find((cat)=>cat.id === b.outlet_review_category_id);

            let a_close = a.project.pipelineProjects?.[0]?.pipelineProjectDates.find((ppd) => (ppd.pipelineMilestone?.milestone?.name.indexOf('Screening') ?? -1) > -1);
            let b_close = b.project.pipelineProjects?.[0]?.pipelineProjectDates.find((ppd) => (ppd.pipelineMilestone?.milestone?.name.indexOf('Screening') ?? -1) > -1);
            if(a_cat?.sort_order !== b_cat?.sort_order){
              return (a_cat?.sort_order ?? -1) > (b_cat?.sort_order ?? -1) ? 1 : -1;
            }
            if (a_close?.date === b_close?.date) {
              return a.project.name > b.project.name ? 1 : -1;
            }
            return (a_close?.date ?? -1) > (b_close?.date ?? -1) ? 1 : -1;
          });
        this.parseItems(sortedTrackers, categories)
    })
  }

  getFundsRequiringReview(id:ID, project:Project, categories: OutletReviewCategory[]):Fund[]{
    let funds: Fund[] = [];
    if(project.outletReviewTrackers){
      let filtered = project.outletReviewTrackers.filter((tracker) => tracker.id !== id);
      if(filtered.length){
        for(let i=0; i<filtered.length; i++){
          let category = categories.find((cat) => cat.id === filtered[i].outlet_review_category_id);
          if(category?.name.indexOf('Third Party') !== -1 && filtered[i].fund){
            funds.push(filtered[i].fund as Fund)
          }
        }
      }
    }
    return funds;
  }

  parseItems(trackers:OutletReviewTracker[], categories: OutletReviewCategory[]){
    let items: TrackerTableItem[] = [];
    let cumulative = 0.0;
    this.totalBeforeClose = 0.0;
    this.total = 0.0;
    //separate calc needed for totals before the rest of it
    for (let i = 0; i< trackers.length; ++i) {
      let tracker = trackers[i];
      let category = categories.find((category)=> category.id === tracker.outlet_review_category_id);
      if((category?.name.indexOf('Prior') ?? -1)  > - 1){
        this.totalBeforeClose += tracker.equity.equity
      }
      this.total += tracker.equity.equity
    }
    for(let i = 0; i< trackers.length; i++) {
      let tracker = trackers[i];
      let funds = this.getFundsRequiringReview(tracker.id, tracker.project, categories);
      let sponsor = tracker.project.sponsor?.[0].sponsor ?? null;
      let percentage = tracker.equity.equity / this.total;
      let committee_date = tracker.project.pipelineProjects?.[0]?.pipelineProjectDates.find((ppd) => (ppd.pipelineMilestone?.milestone?.name.indexOf('Screening') ?? -1) > -1 );
      let close_date = tracker.project.pipelineProjects?.[0]?.pipelineProjectDates.find((ppd) => (ppd.pipelineMilestone?.milestone?.name.indexOf('Clos') ?? -1) > -1);

      cumulative += percentage;
      let item: TrackerTableItem = {
        row: i + 1,
        projectName: tracker.project.name,
        projectUrl: this.dataFormat.formatLink(tracker.project),
        funds,
        outlet_review_category_id: tracker.outlet_review_category_id,
        city: tracker.project.city ?? null,
        state: tracker.project.state?.abbreviation ?? null,
        sponsorName: sponsor ? (sponsor.nickname ? sponsor.nickname : sponsor.name) + (sponsor.is_repeat ? ' (Repeat)' : '') : null,
        sponsorUrl: this.dataFormat.formatLink(sponsor),
        equity: tracker.equity.equity,
        percentage,
        cumulative,
        status: tracker.project.projectStatuses?.[0]?.status?.name ?? '/u2014',
        cohn_reznick_review_status_id: tracker.cohn_reznick_review_status_id,
        committee_memo_status_id: tracker.committee_memo_status_id,
        third_party_review_date_completed: tracker.third_party_review_date_completed,
        outlet_review_tracker_id: tracker.id,
        executive_report_distributed: tracker.executive_report_distributed,
        committee_date: committee_date?.date ?? null,
        committee_is_target: committee_date?.target ?? false,
        close_date: close_date?.date ?? null,
        close_is_target: close_date?.target ?? false,
        investor_committee_date: tracker.investor_committee_date,
        analystName: tracker.outletReviewAnalyst?.name ?? null,
      };
      items.push(item);
    }
    this._items = items;
    this.items.next(this._items);
    this.totalPercentBeforeClose = this.totalBeforeClose / this.total;
    this.totalItems = [
      {label: "Equity Reviewed by Third Party + Memos Distributed PRIOR to Fund Close", equity:this.totalBeforeClose, percentage:this.totalPercentBeforeClose},
      {label: "Total Fund Size (Net of Allowance for Adjustors)", equity:this.total, percentage:this.totalPercent}
    ]
  }
  ngOnDestroy() {
    this.trackerSub?.unsubscribe();
  }
}
